/**
 * Centralized data lookup utilities
 * These functions help resolve IDs to human-readable names across the application
 */

/**
 * Get service name by ID
 * @param {string} serviceId - The service ID to look up
 * @param {Array} services - Array of service objects
 * @returns {string} The service name or fallback text
 */
export const getServiceName = (serviceId, services) => {
  if (!serviceId || !Array.isArray(services)) return "Unknown Service";
  const service = services.find((s) => s.id === serviceId);
  return service ? service.service_name || service.name : "Unknown Service";
};

/**
 * Get doctor name by ID
 * @param {string} doctorId - The doctor ID to look up
 * @param {Array} doctors - Array of doctor objects
 * @returns {string} The doctor name or fallback text
 */
export const getDoctorName = (doctorId, doctors) => {
  if (!doctorId || !Array.isArray(doctors)) return "Unknown Provider";
  const doctor = doctors.find((d) => d.id === doctorId);
  return doctor ? doctor.name : "Unknown Provider";
};

/**
 * Get doctor name by ID or name (flexible lookup)
 * @param {string} providerName - The doctor ID or name to look up
 * @param {Array} doctors - Array of doctor objects
 * @returns {string} The doctor name or fallback text
 */
export const getDoctorNameFlexible = (providerName, doctors) => {
  if (!providerName || !Array.isArray(doctors)) return "Unknown Provider";

  // First try to find by ID (if providerName is actually an ID)
  const doctorById = doctors.find((doctor) => doctor.id === providerName);
  if (doctorById) {
    return doctorById.name;
  }

  // Then try to find by name (if providerName is already a name)
  const doctorByName = doctors.find((doctor) => doctor.name === providerName);
  if (doctorByName) {
    return doctorByName.name;
  }

  // Fallback to original value if not found
  return providerName;
};

/**
 * Get patient name by ID
 * @param {string} patientId - The patient ID to look up
 * @param {Array} patients - Array of patient objects
 * @returns {string} The patient name or fallback text
 */
export const getPatientName = (patientId, patients) => {
  if (!patientId || !Array.isArray(patients)) return "Unknown Patient";
  const patient = patients.find((p) => p.id === patientId);
  return patient ? patient.name : "Unknown Patient";
};

/**
 * Get equipment name by ID
 * @param {string} equipmentId - The equipment ID to look up
 * @param {Array} equipment - Array of equipment objects
 * @returns {string} The equipment name or fallback text
 */
export const getEquipmentName = (equipmentId, equipment) => {
  if (!equipmentId || !Array.isArray(equipment)) return "Unknown Equipment";
  const eq = equipment.find((e) => e.id === equipmentId);
  return eq ? eq.name : "Unknown Equipment";
};

/**
 * Get room name by ID
 * @param {string} roomId - The room ID to look up
 * @param {Array} rooms - Array of room objects
 * @returns {string} The room name or fallback text
 */
export const getRoomName = (roomId, rooms) => {
  if (!roomId || !Array.isArray(rooms)) return "Unknown Room";
  const room = rooms.find((r) => r.id === roomId);
  return room ? room.name : "Unknown Room";
};

/**
 * Get practice name by ID
 * @param {string} practiceId - The practice ID to look up
 * @param {Array} practices - Array of practice objects
 * @returns {string} The practice name or fallback text
 */
export const getPracticeName = (practiceId, practices) => {
  if (!practiceId || !Array.isArray(practices)) return "Unknown Practice";
  const practice = practices.find(
    (p) => p.practiceId === practiceId || p.id === practiceId
  );
  return practice ? practice.practiceName || practice.name : "Unknown Practice";
};

/**
 * Get formatted patient display name (handles patient_name, patient_email, patient_id fallback)
 * @param {Object} item - Object containing patient information
 * @returns {string} The formatted patient display name
 */
export const getPatientDisplayName = (item) => {
  if (!item) return "Unknown Patient";

  // First check if we have a patient_name field
  if (item.patient_name) {
    // Ensure proper spacing in the name
    // Handle cases like "JohnDoe" or "john doe" or "JOHN DOE"
    const name = item.patient_name.trim();

    // If name has no spaces and looks like "FirstnameLastname", try to add space before capital letters
    if (!name.includes(" ") && /[a-z][A-Z]/.test(name)) {
      // Add space before capital letters that follow lowercase letters
      return name.replace(/([a-z])([A-Z])/g, "$1 $2");
    }
    // Otherwise return the name as-is (it probably already has proper spacing)
    return name;
  }

  // If we have patient_email but no name, try to extract a readable name from the email
  if (item.patient_email) {
    // If email looks like "john.doe@example.com", extract "John Doe"
    const emailName = item.patient_email.split("@")[0];
    const formattedName = emailName
      .split(/[._-]/)
      .filter((part) => part.length > 0) // Remove empty parts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
    return formattedName;
  }

  return item.patient_id || "Unknown Patient";
};

/**
 * Get current user's display name (for doctors/users)
 * @param {Object} user - User object from useAuth hook
 * @returns {string} The formatted user display name
 */
export const getUserDisplayName = (user) => {
  if (!user) return "";

  // Try full name first
  if (user.name) {
    return user.name.trim();
  }

  // Try combining firstName and lastName
  if (user.firstName || user.lastName) {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    if (fullName) {
      return fullName;
    }
  }

  // Fallback to email
  if (user.email) {
    return user.email;
  }

  return "";
};
